import React, { useState, useEffect, useRef } from 'react';
import { Send, Camera, Utensils, Loader2 } from 'lucide-react';

export default function CommandCenter({
    onCommand,
    onScan,
    isLoading,
    statusMessage,
    currentView
}) {
    const [input, setInput] = useState('');
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const inputRef = useRef(null);

    const placeholders = [
        "Plan a high-protein Mexican week...",
        currentView === 'wine' ? "Scan a wine label 🍷" : "Scan your fridge 📸",
        "Make Wednesday's dinner vegetarian...",
        "Increase my calorie goal to 2500...",
        "I need a meal plan for Tue-Thu...",
        "Stop suggesting pork...",
        "Suggest a quick energy bowl..."
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [placeholders.length]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;
        onCommand(input.trim());
        setInput('');
    };

    const handleScan = () => {
        if (isLoading) return;
        document.getElementById('command-scan-input').click();
    };

    return (
        <div className="w-full max-w-3xl mx-auto mb-8 relative z-50">
            {/* CHEF'S VOICE / STATUS AREA */}
            <div className={`transition-all duration-500 mb-4 px-2 sm:px-6 flex items-center gap-3 ${(statusMessage || isLoading) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
                <div className="bg-[var(--glass-surface)] backdrop-blur-md border border-[var(--glass-border)] px-4 sm:px-6 py-2 sm:py-3 rounded-2xl shadow-sm inline-flex items-center gap-3 max-w-full">
                    <Utensils className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[var(--accent-color)] animate-pulse flex-shrink-0" />
                    <p className="text-xs sm:text-sm font-bold text-[var(--text-secondary)] italic truncate sm:whitespace-normal">
                        {statusMessage || (isLoading ? "Chef is preparing your kitchen..." : "")}
                    </p>
                </div>
            </div>

            {/* MAIN COMMAND BAR */}
            <form
                onSubmit={handleSubmit}
                aria-label="Chef Command Form"
                className={`relative group glass-panel rounded-2xl sm:rounded-[2rem] p-2 sm:p-3 transition-all focus-within:ring-4 focus-within:ring-[var(--accent-color)]/10 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={handleScan}
                        disabled={isLoading}
                        className={`p-3 sm:p-4 text-[var(--text-secondary)] hover:text-[var(--accent-color)] hover:bg-[var(--accent-color)]/10 rounded-xl sm:rounded-2xl transition-all ${isLoading ? 'cursor-not-allowed' : ''}`}
                        title="Scan Inventory"
                    >
                        <Camera className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>

                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={isLoading}
                        placeholder={isLoading ? "Please wait..." : placeholders[placeholderIndex]}
                        className={`flex-1 bg-transparent border-none focus:ring-0 px-2 sm:px-4 py-3 sm:py-4 text-base sm:text-lg font-bold text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/50 transition-all placeholder:font-normal ${isLoading ? 'cursor-not-allowed' : ''}`}
                    />

                    <button
                        type="submit"
                        disabled={!input.trim() || isLoading}
                        className={`p-3 sm:p-4 rounded-xl sm:rounded-[1.5rem] transition-all ${input.trim() && !isLoading
                            ? 'bg-[var(--text-primary)] text-[var(--glass-surface)] shadow-lg'
                            : 'bg-[var(--text-secondary)]/10 text-[var(--text-secondary)]/50'
                            }`}
                    >
                        {isLoading ? (
                            <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
                        ) : (
                            <Send className="w-5 h-5 sm:w-6 sm:h-6" />
                        )}
                    </button>
                </div>

                {/* HIDDEN SCAN INPUT */}
                <input
                    id="command-scan-input"
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onScan(Array.from(e.target.files))}
                />
            </form>
        </div>
    );
}
